import { computed, Ref, ref } from "vue";
import {
	canDropOnFolder,
	canStartDrag,
	DragPayload,
} from "@/ui/components/FileBrowser/moveRules";
import { folderKey } from "@/ui/components/FileBrowser/types";
import { t } from "@/plugins/i18n";
import ResourceTreeNodeDO = App.Data.ResourceTreeNodeDO;

/**
 * Moving files and folders by dragging, shared by the table, the folder tree
 * and the breadcrumb. All three drop onto a folder, so they share one payload,
 * one validity check and one set of handlers.
 *
 * The payload is held here rather than in `dataTransfer`, which can only carry
 * strings and cannot be read during `dragover` - and the validity of a target
 * has to be known then, to set the drop effect and light the target up.
 * `dataTransfer` still gets a string, because Firefox will not start a drag
 * without one.
 */
export const useBrowserDragDrop = (options: {
	folders: Ref<ResourceTreeNodeDO[]>;
	move: (payload: DragPayload, folderId: number) => Promise<void>;
}) => {
	const payload = ref<DragPayload | null>(null);

	/** Which target is lit up, by `folderKey` so the shared bucket has one too. */
	const activeDropKey = ref<string | null>(null);

	const isDragging = computed(() => payload.value !== null);

	const canDropOn = (target: ResourceTreeNodeDO | null): boolean =>
		canDropOnFolder(payload.value, target, options.folders.value);

	/**
	 * A drag image the browser can photograph. The dragged row is the wrong
	 * picture for a multi-file drag, and an element has to be in the document
	 * for `setDragImage` to capture it.
	 */
	const setDragImage = (event: DragEvent, label: string): void => {
		const ghost = document.createElement("div");

		ghost.textContent = label;
		ghost.style.cssText = [
			"position:absolute",
			"top:-9999px",
			"padding:4px 10px",
			"border-radius:6px",
			"font:600 12px/1.4 system-ui,sans-serif",
			"color:rgb(var(--color-on-surface))",
			"background:rgb(var(--color-surface))",
			"box-shadow:0 2px 8px rgba(0,0,0,0.3)",
			"white-space:nowrap",
		].join(";");

		document.body.appendChild(ghost);
		event.dataTransfer?.setDragImage(ghost, 12, 12);

		// Removed on the next tick: the capture has happened by then, and leaving
		// it in the document would stack up one stray node per drag.
		setTimeout(() => ghost.remove(), 0);
	};

	const beginDrag = (event: DragEvent, label: string): void => {
		event.dataTransfer?.setData("text/plain", label);

		if (event.dataTransfer) {
			event.dataTransfer.effectAllowed = "move";
		}

		setDragImage(event, label);
	};

	/** "Report.pdf" for one thing, "3 items" for anything larger. */
	const labelFor = (dragged: DragPayload): string => {
		const count = dragged.files.length + dragged.folders.length;

		if (count !== 1) {
			return t("fileBrowser.itemCount", count);
		}

		return dragged.files[0]?.title ?? dragged.folders[0].name;
	};

	/**
	 * Nothing here decides what may move - `canStartDrag` does, so the rule the
	 * row's `draggable` attribute was rendered from is the rule enforced when a
	 * drag actually begins.
	 */
	const startDrag = (event: DragEvent, dragged: DragPayload): void => {
		if (!canStartDrag(dragged)) {
			event.preventDefault();

			return;
		}

		payload.value = dragged;
		beginDrag(event, labelFor(dragged));
	};

	/** The folder tree and the breadcrumb drag one folder, never a selection. */
	const startFolderDrag = (
		event: DragEvent,
		folder: ResourceTreeNodeDO
	): void => startDrag(event, { files: [], folders: [folder] });

	/**
	 * Both `dragover` and `dragenter` have to preventDefault on a valid target:
	 * without it the browser treats the element as non-droppable and never
	 * fires `drop`.
	 */
	const onDragOver = (event: DragEvent, target: ResourceTreeNodeDO): void => {
		if (!canDropOn(target)) {
			if (event.dataTransfer) {
				event.dataTransfer.dropEffect = "none";
			}

			return;
		}

		event.preventDefault();
		event.stopPropagation();

		if (event.dataTransfer) {
			event.dataTransfer.dropEffect = "move";
		}

		activeDropKey.value = folderKey(target);
	};

	const onDragLeave = (target: ResourceTreeNodeDO): void => {
		if (activeDropKey.value === folderKey(target)) {
			activeDropKey.value = null;
		}
	};

	const isDropTarget = (target: ResourceTreeNodeDO): boolean =>
		activeDropKey.value === folderKey(target);

	const endDrag = (): void => {
		payload.value = null;
		activeDropKey.value = null;
	};

	const onDrop = async (
		event: DragEvent,
		target: ResourceTreeNodeDO
	): Promise<void> => {
		event.preventDefault();
		event.stopPropagation();

		const dragged = payload.value;

		// Read before clearing: the move is awaited, and a drag that starts in
		// the meantime would otherwise be moving the wrong thing.
		endDrag();

		if (!dragged || !canDropOnFolder(dragged, target, options.folders.value)) {
			return;
		}

		await options.move(dragged, target.id);
	};

	return {
		isDragging,
		canDropOn,
		canStartDrag,
		isDropTarget,
		startDrag,
		startFolderDrag,
		onDragOver,
		onDragLeave,
		onDrop,
		endDrag,
	};
};
